import clsx from 'clsx'
import React from 'react'

type Option<TValue> = {
    label: React.ReactNode
    value: TValue
    disabled?: boolean
    ariaLabel?: string
}

type Props<TValue> = {
    options: Option<TValue>[]
    value: TValue
    onChange: (value: TValue) => void
    name?: string
    className?: string
    size?: 'small' | 'medium'
}

function SegmentedToggleInput<TValue extends string | number | boolean>({
    options,
    value,
    onChange,
    name,
    className,
    size = 'medium',
}: Props<TValue>): React.JSX.Element {
    const optionPadding =
        size === 'small' ? 'px-3 py-1 text-xs' : 'px-4 py-2 text-sm'

    return (
        <div
            className={clsx(
                'inline-flex items-center gap-1 rounded-full border border-gray-200 bg-white p-1 text-gray-700 shadow-inner',
                className,
            )}
        >
            {options.map((option) => {
                const selected = option.value === value

                return (
                    <button
                        key={`${option.value}`}
                        type="button"
                        name={name}
                        aria-pressed={selected}
                        aria-label={option.ariaLabel}
                        disabled={option.disabled}
                        onClick={() => {
                            if (!option.disabled) {
                                onChange(option.value)
                            }
                        }}
                        className={clsx(
                            'rounded-full font-semibold transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ctxt-blue focus-visible:ring-offset-1',
                            optionPadding,
                            selected
                                ? 'bg-ctxt-blue/10 text-ctxt-blue shadow-sm'
                                : 'text-gray-600 hover:bg-gray-50',
                            option.disabled && 'cursor-not-allowed opacity-60',
                        )}
                    >
                        {option.label}
                    </button>
                )
            })}
        </div>
    )
}

export default SegmentedToggleInput
